import type { ComponentType } from 'react'
import type { LucideProps } from 'lucide-react'
import { PrimeMark } from '@/components/PrimeMark'

// The three marks drawn for Prime (2026-10-10). Only the winner, PrimeMark, is
// in the app; the other two live here for the gallery and nowhere else.

const frame = ({ size = 24, strokeWidth = 1.75, absoluteStrokeWidth: _a, color, ...rest }: LucideProps) => ({
  xmlns: 'http://www.w3.org/2000/svg',
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: color ?? 'currentColor',
  strokeWidth,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  ...rest,
})

/** B, Orbit: Prime in the middle, an agent circling it. */
export const OrbitMark: ComponentType<LucideProps> = (p) => (
  <svg {...frame(p)}>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="2.6" fill="currentColor" />
    <circle cx="17.7" cy="6.3" r="1.7" fill="currentColor" />
  </svg>
)

/** C, Rise: a half sun on the horizon, the day's first point. */
export const RiseMark: ComponentType<LucideProps> = (p) => (
  <svg {...frame(p)}>
    <path d="M6 16a6 6 0 0 1 12 0Z" fill="currentColor" />
    <path d="M3 20H21" />
    <path d="M12 4v2.5M4.9 8.9l1.7 1.7M19.1 8.9l-1.7 1.7" />
  </svg>
)

export const PRIME_CANDIDATES: {
  id: string
  name: string
  Mark: ComponentType<LucideProps>
  note: string
}[] = [
  {
    id: 'beacon',
    name: 'A · Beacon',
    Mark: PrimeMark,
    note: 'A four-point star on the horizon line. The app icon’s star, redrawn as one colour.',
  },
  {
    id: 'orbit',
    name: 'B · Orbit',
    Mark: OrbitMark,
    note: 'A dot in a ring with one satellite: the lead and its agents.',
  },
  { id: 'rise', name: 'C · Rise', Mark: RiseMark, note: 'A half sun with three rays on the horizon.' },
]

const SIZES = [16, 24, 48]
const GROUNDS = [
  { name: 'day', bg: '#f7f5f0', fg: '#1d1d1f' },
  { name: 'night', bg: '#15161a', fg: '#ececf0' },
  { name: 'black', bg: '#000', fg: '#fff' },
  { name: 'glass', bg: 'linear-gradient(160deg,#2a2d3a,#0b0c10)', fg: '#f2f2f5' },
]

/** One candidate at 16, 24 and 48px, then on fixed day, night, black and glass grounds. */
export const PrimeCandidates = ({ candidate: c }: { candidate: (typeof PRIME_CANDIDATES)[number] }) => (
  <div className="flex flex-wrap items-end gap-5">
    {SIZES.map((s) => (
      <span key={s} className="flex flex-col items-center gap-1 text-fg">
        <c.Mark size={s} aria-hidden />
        <span className="text-[10px] text-faint">{s}</span>
      </span>
    ))}
    <div className="flex gap-1.5 self-center">
      {GROUNDS.map((g) => (
        <span
          key={g.name}
          className="h-9 w-9 rounded-lg border border-line flex items-center justify-center"
          style={{ background: g.bg, color: g.fg }}
          title={g.name}
        >
          <c.Mark size={20} aria-hidden />
        </span>
      ))}
    </div>
  </div>
)
